import React from 'react';
import { Button, Tooltip } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectedCustomerIdSelector } from '@store/common/selectors';
import { openNewTab } from '@src/utils/dashboard/drilldown';
import { useFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_QUERY } from '@src/utils/sensor/track';
import { logger } from '@src/utils/misc/logger';
import { buildViewerNexaUrl, type SessionDrillInput } from './utils/viewerDeepLink';

interface AskNexaViewerButtonProps {
  viewerId?: string | number | null;
  from?: string | number | null;
  to?: string | number | null;
  size?: 'small' | 'middle' | 'large';
  /**
   * When provided, opens the selected session's diagnostic timeline directly.
   * Absent = viewer landing.
   */
  session?: SessionDrillInput | null;
  /**
   * Compact rendering for tight surfaces (table rows). Drops the "Ask" prefix
   * and the Beta badge so the control takes minimal horizontal space.
   */
  compact?: boolean;
}

/**
 * "Ask Nexa" control that deep-links (new tab) into the nexa-client viewer page,
 * prefilled with the viewer id + date range. Gated on the same flags that govern
 * the destination: unified Nexa must be enabled (the /app/nexa shell) AND the
 * viewer route flag must be on. Hidden otherwise.
 */
const AskNexaViewerButton: React.FC<AskNexaViewerButtonProps> = ({
  viewerId,
  from,
  to,
  size = 'small',
  session = null,
  compact = false,
}) => {
  const nexaUnified = useFeature(featureNames.NEXA_UNIFIED);
  const nexaViewer = useFeature(featureNames.NEXA_VIEWER);
  const customerId = useSelector(selectedCustomerIdSelector) as string | number | null;

  if (!nexaUnified || !nexaViewer) return null;

  // Mirrors isDrillPresent in viewerDeepLink so the tooltip + telemetry match
  // what actually gets serialized into the URL. Requires ms > 0 (rejects
  // negative epochs / NaN / {}).
  const ms = session ? Number(session.sessionStartMs) : NaN;
  const isSessionDrill = Boolean(
    session &&
      session.sessionId !== undefined &&
      session.clientId !== undefined &&
      Number.isFinite(ms) &&
      ms > 0,
  );

  const handleClick = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      const url = buildViewerNexaUrl({ viewerId, from, to, customerId, session });
      apptracker(NEXA_QUERY, {
        value: viewerId ? `${viewerId}` : 'blank',
        source: isSessionDrill ? 'session-deeplink' : 'viewer-deeplink',
      });
      openNewTab(url);
    } catch (error) {
      logger.error('[AskNexaViewerButton] Failed to open viewer deep-link:', error);
    }
  };

  const tooltipTitle = isSessionDrill
    ? 'Ask Nexa to analyze this session'
    : 'Ask Nexa about this viewer';

  if (compact) {
    return (
      <Tooltip title={tooltipTitle}>
        <Button
          type="primary"
          size={size}
          style={{ background: '#0046ff', borderColor: '#0046ff', fontWeight: 500 }}
          onClick={handleClick}
        >
          Nexa
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        type="text"
        size={size}
        icon={<BulbOutlined />}
        style={{ color: '#0046ff' }}
        onClick={handleClick}
      >
        Ask Nexa
        <span
          style={{
            marginLeft: 6,
            fontSize: 9,
            lineHeight: '14px',
            padding: '0 5px',
            background: '#fa541c',
            color: '#fff',
            borderRadius: 3,
            fontWeight: 500,
            letterSpacing: 0.2,
            display: 'inline-block',
            verticalAlign: 'middle',
          }}
        >
          Beta
        </span>
      </Button>
    </Tooltip>
  );
};

export default AskNexaViewerButton;
